import { isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ComponentRef,
    ElementRef,
    HostBinding,
    Injectable,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    PLATFORM_ID,
    REQUEST_CONTEXT,
    ViewChild,
    ViewContainerRef,
    computed,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { AutomationService } from '@frontend/sports/automation/core-feature';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { GridBreakpointOptions, GridBreakpointService } from '@frontend/sports/grid/core/feature/breakpoint';
import { GridBreakpointSize, GridLayout } from '@frontend/sports/grid/core/feature/model';
import { Modal } from '@frontend/sports/modal/feature';
import { PickSourceProvider } from '@frontend/sports/option/pick/feature/tracking';
import { TrackingService } from '@frontend/sports/tracking/feature';
import { Widget, WidgetLayoutTemplate, WidgetLocation, WidgetPage } from '@frontend/sports/types/components/widget';
import {
    ModularConfigAccessorService,
    ModularPickSourceService,
    ModularTrackingService,
    WIDGET_ROUTE_SNAPSHOT_TOKEN,
    WidgetComponent,
    WidgetLoaderService,
    WidgetRefreshService,
    WidgetRegistryService,
    WidgetRouteSnapshot,
} from '@frontend/sports/widget/core/feature';
import { WidgetSkeletonRenderer } from '@frontend/sports/widget/feature/skeleton';
import { SsrPerformanceTrackingService } from '@frontend/vanilla/core';
import { VnRequestContext, linkedTransferState } from '@frontend/vanilla/ssr';
import { isNumber, sortBy } from 'lodash-es';
import { EMPTY, Observable, filter, map } from 'rxjs';

abstract class WidgetRef extends ComponentRef<WidgetComponent<unknown>> {}

interface ReusableWidgetRef {
    config: Widget<unknown>;
    configIndex?: number;
    component?: WidgetRef;
    componentIndex?: number;
}

interface ReusedWidgetRef extends ReusableWidgetRef {
    configIndex: number;
    component: WidgetRef;
    componentIndex: number;
}

@Injectable({ providedIn: 'root' })
export class WidgetSlotGridBreakpointService implements Pick<GridBreakpointService, 'forGrid'> {
    private gridBreakpoint = inject(GridBreakpointService, { skipSelf: true, optional: true });

    private calculateGridWidth: (containerWidth: number) => number;

    constructor() {
        this.forRatio(1);
    }

    forGrid(element: ElementRef<unknown>, layout: GridLayout, options?: GridBreakpointOptions): Observable<GridBreakpointSize> {
        if (!this.gridBreakpoint) {
            return EMPTY;
        }

        return this.gridBreakpoint.forGrid(element, layout, {
            ...options,
            calculateGridWidth: this.calculateGridWidth,
        });
    }

    forRatio(ratio: number): void {
        this.calculateGridWidth = ratio === 1 ? (containerWidth) => containerWidth : (containerWidth) => (containerWidth - 16) * ratio;
    }
}

@Component({
    selector: 'ms-widget-slot',
    template: '<ng-container #container />',
    providers: [
        WidgetSlotGridBreakpointService,
        {
            provide: GridBreakpointService,
            useExisting: WidgetSlotGridBreakpointService,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetSlotComponent implements OnChanges, OnDestroy {
    private componentRefresh = inject(WidgetRefreshService);
    private componentRegistry = inject(WidgetRegistryService);
    private gridBreakpoint = inject(WidgetSlotGridBreakpointService);
    private injector = inject(Injector);
    private widgetLoaderService = inject(WidgetLoaderService);
    private widgetSkeletonRenderer = inject(WidgetSkeletonRenderer);
    private automationService = inject(AutomationService);

    @Input() widget: Widget<unknown>[];
    @Input() parent?: Widget<unknown>;
    @Input() page?: WidgetPage;
    @Input() ratio?: number;
    @Input() layoutTemplate?: WidgetLayoutTemplate;

    @HostBinding('class') className = 'widget-slot';
    @ViewChild('container', { read: ViewContainerRef, static: true }) containerRef: ViewContainerRef;
    private visibleWidgetsTransferState = inject(WidgetSlotTransferState).visibleWidgets;
    private hasVisibleWidgetsTransferState = computed(() => this.visibleWidgetsTransferState().length > 0);
    private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    private components: WidgetRef[] = [];

    private logger: SportsRemoteLogger;
    private ssrPerfTracking = inject(SsrPerformanceTrackingService);

    constructor() {
        const componentRegistry = this.componentRegistry;
        const loggerFactory = inject(LoggerFactory);

        this.logger = loggerFactory.getLogger('WidgetSlotComponent');

        // onChanges is triggered 2 times on page load because of lazy widgets -> careful with lazy loading data in onResolve
        componentRegistry.lazyWidgets$.pipe(takeUntilDestroyed()).subscribe(() => this.createOrUpdateWidgetComponent(true));
    }

    ngOnChanges(): void {
        this.createOrUpdateWidgetComponent();
    }

    createOrUpdateWidgetComponent(isLazy = false) {
        this.gridBreakpoint.forRatio(this.ratio ?? 1);

        const reused: WidgetRef[] = [];

        const ordered: ReusableWidgetRef[] = sortBy(this.widget, (config: Widget<unknown>) => config.order).map((config, index) => {
            const existing = this.components.findIndex((component) => component.instance.config.type === config.type && !reused.includes(component));

            if (existing === -1) {
                return { config };
            }

            reused.push(this.components[existing]);

            return {
                config,
                configIndex: index,
                component: this.components[existing],
                componentIndex: existing,
            };
        });

        this.normalizeReused(ordered, 'configIndex');
        this.normalizeReused(ordered, 'componentIndex');

        this.destroyUnused(reused);
        if (!isLazy) {
            ordered.forEach(({ config }, index) => {
                this.widgetLoaderService.setSlotWidgets(`${config.location}-${config.id}`, {
                    isLoaded: config.location === WidgetLocation.Right,
                    order: index,
                    slot: config.location,
                    widgetId: config.id,
                    parentWidgetId: this.parent?.id,
                });
            });
        }

        // this condition only applies when hydrating from SSR
        // we need to wait until all lazy widgets are registered, otherwise hydration won't work properly
        // widgets need to be rendered in order
        if (this.hasVisibleWidgetsTransferState() && !this.widget.every((widget) => !!this.componentRegistry.get(widget.type))) {
            return;
        }

        ordered
            .filter((widget) => this.componentRegistry.get(widget.config.type) || this.componentRegistry.widgetSkeletons[widget.config.type])
            .forEach(({ config, component }, index) => {
                try {
                    if (component) {
                        this.updateComponent(config, component, !isLazy);

                        this.orderReused(ordered, index);
                    } else {
                        this.createComponent(config, index);
                    }
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (error: any) {
                    this.log(error);
                }
            });

        this.ssrPerfTracking.track('widget-slot');

        this.componentRefresh.registerComponent(this.components.map((component) => component.instance));
    }

    ngOnDestroy(): void {
        this.componentRefresh.deregisterComponent(this.components.map((component) => component.instance));
        this.widgetSkeletonRenderer.removeAllSkeletons();
        this.containerRef.clear();
    }

    private createComponent(config: Widget<unknown>, index: number): void {
        const registeredComponent = this.componentRegistry.get(config.type);
        if (!registeredComponent) {
            if (config.payload) {
                this.widgetSkeletonRenderer.maybeRenderSkeleton({
                    widget: config,
                    allWidgets: this.widget,
                    index,
                    containerRef: this.containerRef,
                    injector: this.injector,
                });
            }

            return;
        }

        const injector: Injector = Injector.create({
            providers: [
                { provide: ModularConfigAccessorService, useClass: ModularConfigAccessorService },
                { provide: PickSourceProvider, useClass: ModularPickSourceService },
                { provide: TrackingService, useClass: ModularTrackingService },
                { provide: Modal, useClass: Modal },
                {
                    provide: WIDGET_ROUTE_SNAPSHOT_TOKEN,
                    useValue: { getActiveRoute: () => this.injector.get(ActivatedRoute).snapshot } satisfies WidgetRouteSnapshot,
                },
            ],
            parent: registeredComponent.injector ?? this.injector,
        });

        this.widgetSkeletonRenderer.removeSkeleton(config.id);
        const component = this.containerRef.createComponent(registeredComponent.componentType, { index, injector });
        this.automationService.addAttr(component.location, config.templateName);
        this.updateComponent(config, component, true);
    }

    private isReused(reused: ReusableWidgetRef): reused is ReusedWidgetRef {
        return reused.component !== undefined;
    }

    private updateComponent(config: Widget<unknown>, widgetComponent: ComponentRef<WidgetComponent<unknown>>, shouldCallOnData: boolean): void {
        try {
            const widget = { ...config, order: this.components.length };
            const accessor = widgetComponent.injector.get(ModularConfigAccessorService);

            accessor.setWidget(widget);
            accessor.setPage(this.page);
            accessor.setLayoutTemplate(this.layoutTemplate);
            accessor.setParentWidget(this.parent);
            const visibleBySsr = this.isBrowser && this.visibleWidgetsTransferState().includes(widget.id);
            if (shouldCallOnData) {
                // eslint-disable-next-line no-param-reassign
                widgetComponent.instance.hidden = !visibleBySsr && config.location !== WidgetLocation.Right;
            }
            if (visibleBySsr) {
                this.visibleWidgetsTransferState.update((widgets) => widgets.filter((id) => id !== widget.id));
            }
            widgetComponent.instance.onResolve(widget, this.parent, shouldCallOnData);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            this.log(error);
            // eslint-disable-next-line no-param-reassign
            widgetComponent.instance.hasData = false;
            this.widgetLoaderService.setWidgetLoaded(`${config.location}-${config.id}`);
        }
        // this is important: update the widgets bindings
        widgetComponent.hostView.detectChanges();
        this.components.push(widgetComponent);
    }

    private orderReused(reused: ReusableWidgetRef[], index: number): void {
        const currentWidget = reused[index];

        if (!this.isReused(currentWidget) || currentWidget.configIndex === currentWidget.componentIndex) {
            return;
        }

        this.containerRef.move(currentWidget.component.hostView, index);
        currentWidget.componentIndex = currentWidget.configIndex;

        reused
            .filter(
                (current) =>
                    this.isReused(current) &&
                    current.configIndex > currentWidget.configIndex &&
                    current.componentIndex < currentWidget.componentIndex,
            )
            .forEach((current) => {
                if (this.isReused(current)) {
                    // eslint-disable-next-line no-param-reassign
                    current.componentIndex++;
                }
            });
    }

    private destroyUnused(reused: ComponentRef<WidgetComponent<unknown>>[]): void {
        const unusedWidget = this.components.filter((component) => !reused.includes(component));

        this.componentRefresh.deregisterComponent(unusedWidget.map((current) => current.instance));
        this.components = [];

        unusedWidget.forEach((component) => component.destroy());
    }

    private normalizeReused(source: ReusableWidgetRef[], target: 'configIndex' | 'componentIndex'): void {
        sortBy(
            source.filter((config) => isNumber(config[target])),
            (config) => config[target],
        ).forEach((config, index) => {
            // eslint-disable-next-line no-param-reassign
            config[target] = index;
        });
    }

    private log(error: Error): void {
        console.error(error);
        this.logger.error(error);
    }
}

@Injectable({ providedIn: 'root' })
class WidgetSlotTransferState {
    /*
     * This service right now only stores information for the Center location, as we
     * only target the mobile experience in the beginning
     */
    visibleWidgets = linkedTransferState<string[]>('visibleWidgets', []);
    private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    private requestContext = inject<VnRequestContext>(REQUEST_CONTEXT);
    private widgetLoaderService = inject(WidgetLoaderService);

    constructor() {
        if (!this.isBrowser) {
            this.widgetLoaderService
                .widgetLoadedDetails$(WidgetLocation.Center)
                .pipe(
                    filter((widgets) => widgets.length > 0),
                    map((widgets) => widgets.filter((w) => w.isVisible).map((w) => w.widgetId as string)),
                    takeUntilDestroyed(),
                )
                .subscribe((widgets) => {
                    this.visibleWidgets.set(widgets);
                    this.requestContext?.logger.info(widgets, 'visibleWidgetsTransferState');
                });
        }
    }
}
